/**
 * Streaming Limits Context Provider
 * Centralized streaming limits management with caching and request deduplication
 * Prevents API spam from multiple component mounts
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, ReactNode } from 'react';
import { streamingLimitsService } from '../services/streamingLimitsService';

interface StreamingLimitsState {
  remainingTime: number;
  dailyLimit: number | null;
  isUnlimited: boolean;
  isLimitExceeded: boolean;
  resetTime: string;
  isBlocked: boolean;
  timeUsed: number;
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;
  lastFetchTime: number;
}

interface StreamingLimitsContextType {
  state: StreamingLimitsState;
  startStreaming: () => void;
  stopStreaming: () => Promise<void>;
  refresh: () => Promise<void>;
  clearBlock: () => Promise<void>;
  updateUnlimitedStatus: (isUnlimited: boolean) => void;
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: any }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'UPDATE_REMAINING_TIME'; payload: number }
  | { type: 'SET_UNLIMITED'; payload: boolean }
  | { type: 'SET_BLOCKED'; payload: boolean }
  | { type: 'SET_STREAMING'; payload: boolean }
  | { type: 'CLEAR_BLOCK' }
  | { type: 'UPDATE_USAGE'; payload: { remainingTime: number; timeUsed: number } };

const BLOCKED_KEY = 'beemi_streaming_blocked';
const CACHE_DURATION = 60000; // 1 minute cache
const SYNC_INTERVAL = 60000; // 60 second sync

const initialState: StreamingLimitsState = {
  remainingTime: 14400, // 4 hours default
  dailyLimit: 14400,
  isUnlimited: false,
  isLimitExceeded: false,
  resetTime: new Date().toISOString(),
  isBlocked: false,
  timeUsed: 0,
  isLoading: false,
  isStreaming: false,
  error: null,
  lastFetchTime: 0,
};

const streamingLimitsReducer = (state: StreamingLimitsState, action: Action): StreamingLimitsState => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };
    
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        remainingTime: action.payload.remainingTime ?? state.remainingTime,
        dailyLimit: action.payload.dailyLimit,
        isUnlimited: action.payload.isUnlimited || false,
        isLimitExceeded: action.payload.remainingTime <= 0,
        isBlocked: action.payload.remainingTime <= 0 ? state.isBlocked : false,
        resetTime: action.payload.resetTime,
        timeUsed: action.payload.timeUsed || 0,
        lastFetchTime: Date.now(),
        error: null,
      };
    
    case 'FETCH_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    
    case 'UPDATE_REMAINING_TIME':
      return {
        ...state,
        remainingTime: Math.max(0, action.payload),
        isLimitExceeded: action.payload <= 0,
      };
    
    case 'SET_UNLIMITED':
      return {
        ...state,
        isUnlimited: action.payload,
        isLimitExceeded: false,
        isBlocked: false,
        dailyLimit: action.payload ? null : state.dailyLimit,
      };
    
    case 'SET_BLOCKED':
      return { ...state, isBlocked: action.payload };
    
    case 'SET_STREAMING':
      return { ...state, isStreaming: action.payload };
    
    case 'CLEAR_BLOCK':
      return { ...state, isBlocked: false };
    
    case 'UPDATE_USAGE':
      return {
        ...state,
        remainingTime: action.payload.remainingTime,
        timeUsed: action.payload.timeUsed,
        isLimitExceeded: action.payload.remainingTime <= 0,
      };
    
    default:
      return state;
  }
};

const StreamingLimitsContext = createContext<StreamingLimitsContextType | undefined>(undefined);

interface StreamingLimitsProviderProps {
  children: ReactNode;
}

export const StreamingLimitsProvider: React.FC<StreamingLimitsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(streamingLimitsReducer, initialState);
  
  const countdownInterval = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const accumulatedSeconds = useRef(0);
  const isSyncing = useRef(false);
  const isFetching = useRef(false); // Prevent concurrent fetches

  /**
   * Check if cache is still fresh
   */
  const isCacheFresh = useCallback((): boolean => {
    return (Date.now() - state.lastFetchTime) < CACHE_DURATION;
  }, [state.lastFetchTime]);

  /**
   * Fetch streaming limits from backend with deduplication
   */
  const fetchLimits = useCallback(async (force = false): Promise<void> => {
    // Use cache if fresh and not forcing
    if (!force && isCacheFresh()) {
      console.log('[StreamingLimits] 📦 Using cached data');
      return;
    }

    // Prevent concurrent fetches
    if (isFetching.current) {
      console.log('[StreamingLimits] ⏳ Fetch already in progress, skipping');
      return;
    }

    isFetching.current = true;
    dispatch({ type: 'FETCH_START' });

    try {
      console.log('[StreamingLimits] 🔄 Fetching from API...');
      const limits = await streamingLimitsService.fetchLimits();
      
      console.log(`[StreamingLimits] ✅ Fetched - Remaining: ${limits.remainingTime}s | Unlimited: ${limits.isUnlimited}`);
      
      // Clear local block if backend shows time available
      if (limits.remainingTime && limits.remainingTime > 0 && !limits.isUnlimited) {
        const blocked = localStorage.getItem(BLOCKED_KEY);
        if (blocked === 'true') {
          console.log('[StreamingLimits] ✅ Clearing local block');
          localStorage.removeItem(BLOCKED_KEY);
        }
      }
      
      dispatch({ type: 'FETCH_SUCCESS', payload: limits });
    } catch (error) {
      console.error('[StreamingLimits] ❌ Fetch failed:', error);
      dispatch({ type: 'FETCH_ERROR', payload: error instanceof Error ? error.message : 'Failed to fetch' });
    } finally {
      isFetching.current = false;
    }
  }, [isCacheFresh]);

  /**
   * Update usage with backend
   */
  const updateUsage = useCallback(async (durationSeconds: number): Promise<void> => {
    if (isSyncing.current || durationSeconds === 0) return;
    
    isSyncing.current = true;
    console.log(`[StreamingLimits] 🔄 Syncing ${durationSeconds}s...`);
    
    try {
      const limits = await streamingLimitsService.updateUsage(durationSeconds);
      
      console.log(`[StreamingLimits] ✅ Synced - Total: ${limits.timeUsed}s | Remaining: ${limits.remainingTime}s`);
      
      dispatch({
        type: 'UPDATE_USAGE',
        payload: {
          remainingTime: limits.remainingTime ?? 0,
          timeUsed: limits.timeUsed || 0
        }
      });

      accumulatedSeconds.current = 0;
    } catch (error) {
      console.error('[StreamingLimits] ❌ Update failed:', error);
    } finally {
      isSyncing.current = false;
    }
  }, []);

  /**
   * Start countdown timer
   */
  const startCountdown = useCallback(() => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }

    countdownInterval.current = setInterval(() => {
      dispatch({ type: 'UPDATE_REMAINING_TIME', payload: state.remainingTime - 1 });
      accumulatedSeconds.current += 1;
    }, 1000);
  }, [state.remainingTime]);

  /**
   * Stop countdown timer
   */
  const stopCountdown = useCallback(() => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  }, []);

  /**
   * Start periodic sync
   */
  const startSync = useCallback(() => {
    if (syncInterval.current) {
      clearInterval(syncInterval.current);
    }

    syncInterval.current = setInterval(async () => {
      if (accumulatedSeconds.current > 0) {
        await updateUsage(accumulatedSeconds.current);
      }
    }, SYNC_INTERVAL);
  }, [updateUsage]);

  /**
   * Stop periodic sync
   */
  const stopSync = useCallback(() => {
    if (syncInterval.current) {
      clearInterval(syncInterval.current);
      syncInterval.current = null;
    }
  }, []);

  /**
   * Start streaming session
   */
  const startStreaming = useCallback(() => {
    if (state.isBlocked || state.isLimitExceeded || state.remainingTime <= 0) {
      console.log(`[StreamingLimits] ⛔ Cannot start - Blocked: ${state.isBlocked}`);
      return;
    }

    console.log(`[StreamingLimits] ▶️ Starting streaming - Remaining: ${state.remainingTime}s`);
    dispatch({ type: 'SET_STREAMING', payload: true });
    accumulatedSeconds.current = 0;
    startCountdown();
    startSync();
  }, [state.isBlocked, state.isLimitExceeded, state.remainingTime, startCountdown, startSync]);

  /**
   * Stop streaming session
   */
  const stopStreaming = useCallback(async () => {
    console.log(`[StreamingLimits] ⏸️ Stopping - Final sync: ${accumulatedSeconds.current}s`);
    dispatch({ type: 'SET_STREAMING', payload: false });
    stopCountdown();
    stopSync();

    if (accumulatedSeconds.current > 0) {
      await updateUsage(accumulatedSeconds.current);
    }
  }, [stopCountdown, stopSync, updateUsage]);

  /**
   * Refresh limits from backend
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchLimits(true);
  }, [fetchLimits]);

  /**
   * Clear blocked state
   */
  const clearBlock = useCallback(async (): Promise<void> => {
    dispatch({ type: 'CLEAR_BLOCK' });
    localStorage.removeItem(BLOCKED_KEY);
  }, []);

  /**
   * Update unlimited status (called from SubscriptionContext if exists)
   */
  const updateUnlimitedStatus = useCallback((isUnlimited: boolean) => {
    console.log(`[StreamingLimits] ${isUnlimited ? '💎' : '🆓'} Unlimited status: ${isUnlimited}`);
    dispatch({ type: 'SET_UNLIMITED', payload: isUnlimited });
    if (isUnlimited) {
      clearBlock();
    }
  }, [clearBlock]);

  /**
   * Check blocked state on mount
   */
  useEffect(() => {
    const checkBlocked = async () => {
      try {
        const blocked = localStorage.getItem(BLOCKED_KEY);
        if (blocked === 'true') {
          dispatch({ type: 'SET_BLOCKED', payload: true });
        }
      } catch (error) {
        console.error('[StreamingLimits] Failed to check blocked state:', error);
      }
    };

    checkBlocked();
  }, []);

  /**
   * Fetch limits on mount
   */
  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  /**
   * Handle limit exceeded
   */
  useEffect(() => {
    if (state.isLimitExceeded && !state.isBlocked && !state.isUnlimited && state.isStreaming) {
      console.log('[StreamingLimits] 🚫 LIMIT EXCEEDED - Blocking');
      dispatch({ type: 'SET_BLOCKED', payload: true });
      localStorage.setItem(BLOCKED_KEY, 'true');
      stopStreaming();
    }
  }, [state.isLimitExceeded, state.isBlocked, state.isUnlimited, state.isStreaming, stopStreaming]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopCountdown();
      stopSync();
    };
  }, [stopCountdown, stopSync]);

  const value: StreamingLimitsContextType = {
    state,
    startStreaming,
    stopStreaming,
    refresh,
    clearBlock,
    updateUnlimitedStatus,
  };

  return (
    <StreamingLimitsContext.Provider value={value}>
      {children}
    </StreamingLimitsContext.Provider>
  );
};

/**
 * Hook to access streaming limits context
 */
export const useStreamingLimitsContext = (): StreamingLimitsContextType => {
  const context = useContext(StreamingLimitsContext);
  
  if (context === undefined) {
    throw new Error('useStreamingLimitsContext must be used within StreamingLimitsProvider');
  }
  
  return context;
};
